import { Link } from "react-router-dom";

export function PageHeader({ overline, title, description, actions, testId }) {
  return (
    <header
      className="mb-7 flex flex-col-reverse md:flex-row md:items-end md:justify-between gap-4"
      data-testid={testId}
    >
      <div>
        {overline && (
          <p
            className="text-[10px] uppercase tracking-[0.24em] text-sky-700 font-bold mb-2"
            style={{ fontFamily: "Outfit, ui-sans-serif" }}
          >
            {overline}
          </p>
        )}
        <h1
          className="font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight"
          style={{ fontFamily: "Outfit, ui-sans-serif" }}
        >
          {title}
        </h1>
        {description && (
          <p className="mt-2 text-slate-500 text-[15px] leading-relaxed max-w-2xl">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  );
}

export function StatPill({ label, value, hint, accent = "sky", icon: Icon }) {
  const palette = {
    sky: "bg-sky-50 text-sky-700 border-sky-100",
    gold: "bg-amber-50 text-amber-700 border-amber-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.06)]">
      <div className="flex items-center justify-between mb-4">
        <span
          className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500"
          style={{ fontFamily: "Outfit, ui-sans-serif" }}
        >
          {label}
        </span>
        {Icon && (
          <span className={`inline-flex items-center justify-center h-8 w-8 rounded-lg border ${palette[accent]}`}>
            <Icon size={14} />
          </span>
        )}
      </div>
      <p
        className="text-3xl font-extrabold text-slate-900 tracking-tight"
        style={{ fontFamily: "Outfit, ui-sans-serif" }}
      >
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export function StatusBadge({ status }) {
  const palette = {
    scheduled: "bg-sky-50 text-sky-700 border-sky-200",
    in_progress: "bg-amber-50 text-amber-700 border-amber-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    skipped: "bg-slate-100 text-slate-500 border-slate-200",
    canceled: "bg-rose-50 text-rose-600 border-rose-200",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    past_due: "bg-rose-50 text-rose-600 border-rose-200",
    new: "bg-sky-50 text-sky-700 border-sky-200",
    contacted: "bg-amber-50 text-amber-700 border-amber-200",
    quoted: "bg-violet-50 text-violet-700 border-violet-200",
    closed: "bg-slate-100 text-slate-500 border-slate-200",
    mock_sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    failed: "bg-rose-50 text-rose-600 border-rose-200",
    queued: "bg-slate-100 text-slate-500 border-slate-200",
    manual_unpaid: "bg-violet-50 text-violet-700 border-violet-200",
    manual_pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
    paused: "bg-amber-50 text-amber-700 border-amber-200",
    skipped_paused: "bg-slate-100 text-slate-500 border-slate-200",
    archived: "bg-zinc-100 text-zinc-600 border-zinc-300",
  };
  const cls = palette[status] || "bg-slate-100 text-slate-500 border-slate-200";
  const label = (status || "—").replace(/_/g, " ");
  return (
    <span
      className={`inline-flex items-center text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-md border ${cls}`}
      data-testid={`status-badge-${status}`}
    >
      {label}
    </span>
  );
}

export function Panel({ children, className = "", testId }) {
  return (
    <section
      className={`bg-white border border-slate-200 rounded-xl shadow-[0_2px_8px_-4px_rgba(15,23,42,0.06)] ${className}`}
      data-testid={testId}
    >
      {children}
    </section>
  );
}

export function EmptyState({ title, description, action }) {
  return (
    <div className="text-center py-14 px-6 bg-white border border-dashed border-slate-200 rounded-xl">
      <h3
        className="font-bold text-slate-900 text-lg"
        style={{ fontFamily: "Outfit, ui-sans-serif" }}
      >
        {title}
      </h3>
      {description && (
        <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
          {description}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function MonoCell({ children }) {
  return (
    <span
      className="text-xs text-slate-500"
      style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
    >
      {children}
    </span>
  );
}

export function adminCurrency(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return iso;
  const diff = Math.round((t - Date.now()) / 1000);
  const abs = Math.abs(diff);
  const map = [
    [60, "second"],
    [60, "minute"],
    [24, "hour"],
    [7, "day"],
    [4.345, "week"],
    [12, "month"],
    [Number.POSITIVE_INFINITY, "year"],
  ];
  let value = abs;
  let unit = "second";
  for (const [step, name] of map) {
    if (value < step) {
      unit = name;
      break;
    }
    value /= step;
  }
  const rounded = Math.round(value);
  if (unit === "second" && rounded < 5) return diff >= 0 ? "just now" : "moments ago";
  return diff >= 0 ? `in ${rounded} ${unit}${rounded === 1 ? "" : "s"}` : `${rounded} ${unit}${rounded === 1 ? "" : "s"} ago`;
}

export function shortDate(iso) {
  if (!iso) return "—";
  // R5 — strict date-only values ("YYYY-MM-DD") represent calendar
  // days, not UTC instants. Rendering them via
  // `new Date(iso).toLocaleDateString(...)` without a timeZone option
  // silently shifts them backwards in America/New_York (Aug 14 stored
  // -> Aug 13 displayed). Detect the strict shape and format as UTC
  // so the displayed calendar date matches the stored value in every
  // browser timezone.
  if (isDateOnly(iso)) {
    const t = new Date(iso + "T00:00:00Z");
    if (isNaN(t.getTime())) return iso;
    return t.toLocaleDateString("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const t = new Date(iso);
  if (isNaN(t.getTime())) return iso;
  return t.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// R5 — strict YYYY-MM-DD detector. Rejects timestamps, alternate
// separators, and impossible dates (e.g. 2026-02-30). Silent
// normalization is prevented by the round-trip check.
export function isDateOnly(value) {
  if (typeof value !== "string" || value.length !== 10) return false;
  if (value[4] !== "-" || value[7] !== "-") return false;
  const y = value.slice(0, 4);
  const m = value.slice(5, 7);
  const d = value.slice(8, 10);
  if (!/^\d{4}$/.test(y) || !/^\d{2}$/.test(m) || !/^\d{2}$/.test(d)) return false;
  const t = new Date(value + "T00:00:00Z");
  if (isNaN(t.getTime())) return false;
  // Round-trip: reject impossible dates that JS would silently normalize.
  const iso = t.toISOString().slice(0, 10);
  return iso === value;
}

// R5 — derive the actual weekday of a stored strict-date-only value
// without any browser-timezone shift. Returns short names
// ("Mon".."Sun") to match the customer service_day vocabulary. Returns
// an empty string for malformed input, timestamp-shaped input, or a
// non-string value. Never derives from service_day.
const _WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export function weekdayFromDateOnly(value) {
  if (!isDateOnly(value)) return "";
  const t = new Date(value + "T00:00:00Z");
  return _WEEKDAYS_SHORT[t.getUTCDay()];
}

export function PrimaryButton({ children, ...rest }) {
  return (
    <button
      type="button"
      className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-semibold text-sm h-10 px-4 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_4px_14px_-6px_rgba(14,165,233,0.6)]"
      {...rest}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, ...rest }) {
  return (
    <button
      type="button"
      className="inline-flex items-center justify-center gap-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 font-semibold text-sm h-10 px-4 border border-slate-200 transition-colors disabled:opacity-60"
      {...rest}
    >
      {children}
    </button>
  );
}

export function RowLink({ to, children, testId }) {
  return (
    <Link to={to} data-testid={testId} className="block group">
      {children}
    </Link>
  );
}
